import React, { useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import settings, { AmenityType } from "../../apiConfigs/settings";
import { ReactComponent as Delete } from "../../assets/delete.svg";
import { ReactComponent as Edit } from "../../assets/edit.svg";
import { ReactComponent as Next } from "../../assets/next.svg";
import { ReactComponent as Prev } from "../../assets/prev.svg";
import { ReactComponent as View } from "../../assets/view.svg";
import {
  ButtonsSection,
  LabelText,
  ValueText,
} from "../../pages/Properties/Details/styles";
import { modifyNumberWithSuffix } from "../../utils/conversion";
import requestAPI from "../../utils/requestAPI";
import {
  getIconsUrl,
  resizeImage,
  resizeMultipleImages,
} from "../../utils/resizeImages";
import Button from "../Button";
import DeleteModal from "../DeleteModal";
import Input from "../Input";
import Modal from "../Modal";
import Typography from "../Typography";

import Viewer from "react-viewer";
import { colors } from "../../theme/colors";
import { IUserRoleBasedSetting, IUserType } from "../../@types/userTypes";
import Flex from "../Flex";

interface EyeIconProps {
  size: number;
}

const EducationContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EducationItem = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #f9f9fd;
  padding: 10px;
  border-radius: 8px;
`;

// Education interface
interface Education {
  school: string;
  degree: string;
  fieldOfStudy: string;
  currentlyStudying: boolean;
  startMonth: string;
  startYear: string;
  endMonth: string;
  endYear: string;
  _id: string;
}
interface EmploymentHistory {
  jobDesignation: string;
  companyName: string;
  companyAddress: string;
  currentlyWorking: boolean;
  startMonth: string;
  startYear: string;
  address: string;
  _id: string;
}

const formatStatus = (status: string): string => {
  if (!status) return status;

  // Check if the status is in uppercase with underscores
  const isUpperCaseWithUnderscores = /^[A-Z_]+$/.test(status);

  if (isUpperCaseWithUnderscores) {
    return status
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
  }

  return status; // Return the status as is if it's already in the desired format
};
export const renderRole = (role: string | string[]) => {
  const renderSingleRole = (role: string) => {
    return (
      <div
        style={{
          borderRadius: 25,
          height: 30,
          padding: 8,
          borderWidth: 1,
          borderStyle: "solid",
          borderColor:
            role === "admin"
              ? "#C8EDFF"
              : role === "CUSTOMERSUPPORT"
              ? "#B5F5EC"
              : role === "LANDLORD"
              ? "#B5F5EC"
              : role === "OPERATOR"
              ? "#FFFB8F"
              : "#FCFFE6",
          backgroundColor:
            role === "admin"
              ? "#E6F7FF"
              : role === "CUSTOMERSUPPORT"
              ? "#E6FFFB"
              : role === "LANDLORD"
              ? "#E6FFFB"
              : role === "OPERATOR"
              ? "#FFFDD4"
              : "#FCFFE6",
          display: "inline-flex",
          justifyContent: "center",
          alignItems: "center",
          marginRight: 8,
        }}
      >
        <Typography
          fontFamily="urbanist"
          style={{
            fontSize: 12,
            fontWeight: 500,
            color:
              role === "admin"
                ? "#096DD9"
                : role === "CUSTOMERSUPPORT"
                ? "#08979C"
                : role === "LANDLORD"
                ? "#08979C"
                : role === "OPERATOR"
                ? "#D4B106"
                : "#7CB305",
          }}
        >
          {formatStatus(role)}
        </Typography>
      </div>
    );
  };

  if (Array.isArray(role)) {
    return (
      <div style={{ display: "flex" }}>
        {role.map((r, index) => (
          <React.Fragment key={index}>{renderSingleRole(r)}</React.Fragment>
        ))}
      </div>
    );
  }

  // If `role` is a single string, render it as usual.
  return renderSingleRole(role);
};
export const renderRoleSettings = (
  roleBasedSettings: IUserRoleBasedSetting[]
) => {
  const renderSingleRoleSettings = (
    roleBasedSettings: IUserRoleBasedSetting
  ) => {
    const {
      role,
      isBlocked,
      deletionInfo,
      onboardingCompleted,
      visibleProfile,
      invitePermission,
    } = roleBasedSettings;
    return (
      <div>
        <Typography
          fontFamily="urbanist"
          style={{
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          {formatStatus(role)}
        </Typography>
        {[
          { label: "Blocked", value: isBlocked ? "Yes" : "No" },
          {
            label: "Deletion Requested",
            value: deletionInfo?.requestedAt ? "Yes" : "No",
          },
          {
            label: "Onboarding Completed",
            value: onboardingCompleted ? "Yes" : "No",
          },
          { label: "Profile Visible", value: visibleProfile ? "Yes" : "No" },
          {
            label: "Invitation Permission",
            value: invitePermission ? "Yes" : "No",
          },
        ].map(({ label, value }) => (
          <Flex direction="row" justifyContent="space-between">
            <Typography
              fontFamily="urbanist"
              style={{
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {label}
            </Typography>
            <Typography
              fontFamily="urbanist"
              style={{
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {value}
            </Typography>
          </Flex>
        ))}
      </div>
    );
  };

  if (Array.isArray(roleBasedSettings)) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <Typography>Role Settings</Typography>
        {roleBasedSettings.map((r, index) => (
          <React.Fragment key={index}>
            {renderSingleRoleSettings(r)}
          </React.Fragment>
        ))}
      </div>
    );
  }
  return <p>N/A</p>;
};

export const renderAction = ({
  onClickView,
  onClickEdit,
  onClickDelete,
}: {
  onClickView: () => void;
  onClickEdit: () => void;
  onClickDelete: () => void;
}) => {
  return (
    <div style={{ display: "flex", gap: 20 }}>
      <div
        onClick={() => {
          // navigate(`users/${id}`);
          onClickView();
        }}
      >
        <View fill="#7E7E7E"></View>
      </div>
      <div
        onClick={() => {
          // navigate(`users/${id}`);
          onClickEdit();
        }}
      >
        <Edit fill="#7E7E7E"></Edit>
      </div>
      <div
        onClick={() => {
          // navigate(`users/${id}`);
          onClickDelete();
        }}
      >
        <Delete fill="#7E7E7E"></Delete>
      </div>
    </div>
  );
};
export const renderstatus = (status?: string) => {
  return (
    <div
      style={{
        borderRadius: 25,
        height: 30,
        padding: 8,
        backgroundColor:
          status === "ACTIVE"
            ? "#FCFFE6"
            : status === "INACTIVE"
            ? "#FAEEF6"
            : status === "SECURITY_DEPOSITED"
            ? "#FCFFE6"
            : status === "SUSPENDED"
            ? "#FFF1F0"
            : status === "CLOSED"
            ? "#FFF1F0"
            : status === "REJECTED"
            ? "#FFF1F0"
            : status === "DRAFT"
            ? "#FFFDD4"
            : status === "CONTRACT_SIGNED"
            ? "#FFFDD4"
            : status === "CONTRACT_GENERATED"
            ? "#FFFDD4"
            : status === "BOOKED"
            ? "#FCFFE6"
            : status === "UNDER_REVIEW"
            ? "#E6FFFB"
            : status === "REQUESTED"
            ? "#FAEEF6"
            : status === "OPEN"
            ? "#FCFFE6"
            : status === "Open"
            ? "#FCFFE6"
            : status === "ACCEPTED"
            ? "#FCFFE6"
            : status === "UNDER_CONTRACT"
            ? "#D1E7FF"
            : status === "PENDING"
            ? "#FAEEF6"
            : status === "PAID"
            ? "#D9F7D9"
            : status === "DUE"
            ? "#FFF1B0"
            : status === "PROCESSING"
            ? "#FFF1B0"
            : "",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid",
        borderColor:
          status === "ACTIVE"
            ? "#F4FFB8"
            : status === "INACTIVE"
            ? "#F4DEEC"
            : status === "SUSPENDED"
            ? "#FFCCC7"
            : status === "REJECTED"
            ? "#FFCCC7"
            : status === "DRAFT"
            ? "#FFFB8F"
            : status === "CLOSED"
            ? "#FFFB8F"
            : status === "CONTRACT_SIGNED"
            ? "#FFFB8F"
            : status === "BOOKED"
            ? "#F4FFB8"
            : status === "UNDER_REVIEW"
            ? "#F4FFB8"
            : status === "CONTRACT_GENERATED"
            ? "#B5F5EC"
            : status === "REQUESTED"
            ? "#F4DEEC"
            : status === "OPEN"
            ? "#F4DEEC"
            : status === "Open"
            ? "#F4DEEC"
            : status === "UNDER_CONTRACT"
            ? "#A5D8FF"
            : status === "PENDING"
            ? "#F4DEEC"
            : status === "PAID"
            ? "#91F294"
            : status === "ACKNOWLEDGED"
            ? "#91F294"
            : status === "DUE"
            ? "#FFDC6D"
            : status === "PROCESSING"
            ? "#FFDC6D"
            : status === "SECURITY_DEPOSITED"
            ? "#91F294"
            : "",
      }}
    >
      <Typography
        style={{
          fontSize: 14,
          fontWeight: 800,
          color:
            status === "ACTIVE"
              ? "#7CB305"
              : status === "INACTIVE"
              ? "#CF69AB"
              : status === "SUSPENDED"
              ? "#F5222D"
              : status === "REJECTED"
              ? "#F5222D"
              : status === "CLOSED"
              ? "#F5222D"
              : status === "DRAFT"
              ? "#D4B106"
              : status === "CONTRACT_SIGNED"
              ? "#D4B106"
              : status === "CONTRACT_GENERATED"
              ? "#7CB305"
              : status === "BOOKED"
              ? "#7CB305"
              : status === "ACCEPTED"
              ? "#F5222D"
              : status === "OPEN"
              ? "#7CB305"
              : status === "Open"
              ? "#7CB305"
              : status === "UNDER_REVIEW"
              ? "#08979C"
              : status === "REQUESTED"
              ? "#CF69AB"
              : status === "ACKNOWLEDGED"
              ? "#CF69AB"
              : status === "UNDER_CONTRACT"
              ? "#1E90FF"
              : status === "PENDING"
              ? "#CF69AB"
              : status === "PAID"
              ? "#52C41A" // Green color for PAID
              : status === "DUE"
              ? "#FFAA00" // Orange color for DUE
              : status === "PROCESSING"
              ? "#FFAA00" // Orange color for DUE
              : status === "SECURITY_DEPOSITED"
              ? "#91F294"
              : "",
        }}
      >
        {formatStatus(status || "")}
      </Typography>
    </div>
  );
};

export const renderFullDetails = ({
  avatar,
  firstName,
  email,
  lastName,
}: {
  avatar?: string;
  firstName?: string;
  email?: string;
  lastName?: string;
}) => {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
      <img
        src={
          resizeImage(avatar ?? "", "sm") ||
          "https://e7.pngegg.com/pngimages/84/165/png-clipart-united-states-avatar-organization-information-user-avatar-service-computer-wallpaper-thumbnail.png"
        }
        width={40}
        height={40}
        style={{ borderRadius: "50%" }}
        alt="avatar"
      />

      <div style={{ display: "flex", flexDirection: "column" }}>
        {/* Handling null or undefined for firstName and lastName */}
        <Typography
          fontFamily="urbanist"
          style={{
            color: "#5F5F5F",
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          {firstName && lastName ? `${firstName} ${lastName}` : "Unknown User"}{" "}
        </Typography>
        <Typography
          fontFamily="urbanist"
          style={{ color: "#6D718B", fontSize: 12, overflow: "clip" }}
        >
          {email || "No Email Provided"}{" "}
        </Typography>
      </div>
    </div>
  );
};

const EyeIcon: React.FC<EyeIconProps> = ({ size }) => (
  <View
    fill="grey"
    style={{
      width: size,
      height: size,
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 2,
    }}
  ></View>
);

interface RenderImagesProps {
  images?: string[];
  displayImage?: boolean;
  resize?: boolean;
}

export const RenderImages: React.FC<RenderImagesProps> = ({
  images = [],
  displayImage,
  resize = true,
}) => {
  const [imagePreviewIndex, setImagePreviewIndex] = useState<number | null>(
    null
  );

  const openImagePreview = (index: number) => {
    setImagePreviewIndex(index);
  };

  let maxImages = images.slice(0, 3);
  if (images?.length === 0 || images === null) {
    return (
      <div
        style={{
          background: "#dbe0f0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          padding: "36px 10px",
          borderRadius: "8px",
        }}
      >
        <LabelText style={{ color: colors.disableText }}>
          No Images for display
        </LabelText>
      </div>
    );
  }

  return (
    <>
      {maxImages?.map((item: string, index: number) => (
        <div
          key={index}
          style={{
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => openImagePreview(index)}
        >
          <img
            src={item}
            width={displayImage ? 100 : 50}
            height={displayImage ? 100 : 50}
            alt={`${index}`}
            style={{
              borderRadius: 10,
              transition: "0.3s ease",
              filter: "grayscale(0%)",
            }}
            onMouseEnter={(e) => {
              const target = e.currentTarget as HTMLImageElement;
              target.style.filter = "grayscale(70%)";
              const eyeIcon = target.nextSibling as HTMLElement;
              if (eyeIcon) eyeIcon.style.display = "block";
            }}
            onMouseLeave={(e) => {
              const target = e.currentTarget as HTMLImageElement;
              target.style.filter = "grayscale(0%)";
              const eyeIcon = target.nextSibling as HTMLElement;
              if (eyeIcon) eyeIcon.style.display = "none";
            }}
          />

          {displayImage && (
            <div
              style={{
                display: "none",
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1,
              }}
            >
              <EyeIcon size={40} />
            </div>
          )}
        </div>
      ))}

      <Viewer
        visible={imagePreviewIndex !== null}
        activeIndex={imagePreviewIndex ?? 0}
        onClose={() => setImagePreviewIndex(null)}
        onMaskClick={() => setImagePreviewIndex(null)}
        images={images?.map((img) => ({
          src: resize ? resizeImage(img, "lg") ?? "" : getIconsUrl(img),
        }))}
        zIndex={999999999}
        rotatable={false}
        scalable={false}
      />
    </>
  );
};
export const RenderReviewImages: React.FC<RenderImagesProps> = ({
  images,
  displayImage,
  resize = true,
}) => {
  const [imagePreviewIndex, setImagePreviewIndex] = useState<number | null>(
    null
  );

  const openImagePreview = (index: number) => {
    setImagePreviewIndex(index);
  };

  let maxImages = displayImage
    ? resize
      ? resizeMultipleImages(images, "lg")
      : images
    : resize
    ? resizeMultipleImages((images || []).slice(0, 3), "sm")
    : images?.slice(0, 3);

  if (images?.length === 0 || images === null) {
    return <LabelText>No Images for display</LabelText>;
  }

  return (
    <>
      {maxImages?.map((item: string, index: number) => (
        <div
          key={index}
          style={{
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => openImagePreview(index)}
        >
          <img
            src={item}
            width={displayImage ? 180 : 50}
            height={displayImage ? 180 : 50}
            alt={`${index}`}
            style={{
              objectFit: "cover",
              borderRadius: 10,
              transition: "0.3s ease",
              filter: "grayscale(0%)",
            }}
            onMouseEnter={(e) => {
              const target = e.currentTarget as HTMLImageElement;
              target.style.filter = "grayscale(70%)";
              const eyeIcon = target.nextSibling as HTMLElement;
              if (eyeIcon) eyeIcon.style.display = "block";
            }}
            onMouseLeave={(e) => {
              const target = e.currentTarget as HTMLImageElement;
              target.style.filter = "grayscale(0%)";
              const eyeIcon = target.nextSibling as HTMLElement;
              if (eyeIcon) eyeIcon.style.display = "none";
            }}
          />

          {displayImage && (
            <div
              style={{
                display: "none",
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1,
              }}
            >
              <EyeIcon size={40} />
            </div>
          )}
        </div>
      ))}

      <Viewer
        visible={imagePreviewIndex !== null}
        activeIndex={imagePreviewIndex ?? 0}
        onClose={() => setImagePreviewIndex(null)}
        onMaskClick={() => setImagePreviewIndex(null)}
        images={images?.map((img) => ({
          src: resize ? resizeImage(img, "lg") ?? "" : getIconsUrl(img),
        }))}
        zIndex={999999999}
        rotatable={false}
        scalable={false}
      />
    </>
  );
};

export const RenderObject: React.FC<any> = ({ objects }) => {
  if (!objects || objects?.length === 0) {
    return <ValueText>No data available</ValueText>;
  }

  return (
    <div>
      {objects.map((obj: any, index: any) => (
        <div key={index} style={{ marginBottom: "20px" }}>
          <Typography fontWeight="normal" style={{ color: "#5f5f5f" }}>
            {obj.title}{" "}
          </Typography>
          <Typography fontWeight="normal" style={{ color: "#5f5f5f" }}>
            {obj.description}
          </Typography>
        </div>
      ))}
    </div>
  );
};
export const RenderAmenityObject: React.FC<any> = ({
  objects,
  enableDelete,
  refetch,
}) => {
  const [openDelete, setDeleteModal] = useState<string | null>(null);
  const [openUpdate, setUpdateModal] = useState<string | null>(null);
  const [orderValue, setOrderValue] = useState(0);
  if (!objects || objects?.length === 0) {
    return <ValueText>No data available</ValueText>;
  }
  const handleDelete = async (id: string) => {
    try {
      await requestAPI(settings.deleteAmenity(id));
      toast.success("Succesfully Deleted");
      setDeleteModal(null);
      refetch();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const updateOrder = async (id: string, data: AmenityType) => {
    try {
      await requestAPI(settings.updateAmenity(id, data));
      toast.success("Succesfully Updated");
      setUpdateModal(null);
      refetch();
    } catch (error: any) {
      toast.error(error.message);
    }
  };
  return (
    <div
      style={{
        display: "flex",
        gap: "10px",
        flexWrap: "wrap",
        flexDirection: "column",
      }}
    >
      {enableDelete && (
        <ButtonsSection>
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <Typography style={{ color: "#5f5f5f" }}>Amenity Title</Typography>
          </div>
          {enableDelete && (
            <div style={{ display: "flex", gap: "30px", alignItems: "center" }}>
              <Typography
                style={{
                  color: "#5f5f5f",
                  width: "75px",
                  textAlign: "center",
                }}
              >
                Order
              </Typography>

              <div
                style={{
                  cursor: "pointer",
                  width: "90px",
                  textAlign: "center",
                }}
              >
                <Typography>Actions</Typography>
              </div>
            </div>
          )}
        </ButtonsSection>
      )}
      {objects?.map((item: any, index: number) => (
        <ButtonsSection>
          <div
            key={index}
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <div
              style={{
                borderRadius: 50,
                width: 50,
                height: 50,
                border: "1px solid #5f5f5d",
                display: "flex", // Added to enable flexbox
                alignItems: "center", // Centers the image vertically
                justifyContent: "center", // Centers the image horizontally
              }}
            >
              <img src={getIconsUrl(item.icon)} width={30} height={30} />
            </div>

            <Typography style={{ color: "#5f5f5f" }}>{item.title}</Typography>
          </div>
          {enableDelete && (
            <div style={{ display: "flex", gap: "30px", alignItems: "center" }}>
              {item?.order > 0 && (
                <Typography
                  style={{
                    color: "#5f5f5f",
                    textAlign: "center",
                    width: "75px",
                  }}
                >
                  {modifyNumberWithSuffix(item.order)}
                </Typography>
              )}

              <div
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setUpdateModal(item._id);
                  setOrderValue(item?.order || 0);
                }}
              >
                <Edit fill="rgb(53, 65, 90)"></Edit>
              </div>
              <div
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setDeleteModal(item._id);
                }}
              >
                <Delete fill="rgb(226, 32, 32)"></Delete>
              </div>
            </div>
          )}
          <Modal
            halfWidth
            isOpen={openUpdate === item._id}
            onClose={() => setUpdateModal(null)}
            title={`Update Amenity order ${item.title}`}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 20,
                }}
              >
                <Typography size={"heading"} fontWeight="bold">
                  Order
                </Typography>
                <Input
                  value={orderValue || 0}
                  onChange={(e) => {
                    const { value } = e.target;
                    setOrderValue(parseInt(value, 10) || 0);
                  }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  onClick={() =>
                    updateOrder(item?._id, {
                      title: item.title,
                      icon: item.icon,
                      order: orderValue,
                    })
                  }
                >
                  Update
                </Button>
              </div>
            </div>
          </Modal>
          <DeleteModal
            isOpen={openDelete === item._id}
            itemName="Amenity"
            loading={false}
            onClose={() => {
              setDeleteModal(null);
            }}
            title="Are you sure you want to delete?"
            onDelete={() => {
              handleDelete(item._id);
            }}
          ></DeleteModal>
        </ButtonsSection>
      ))}
    </div>
  );
};

export const EducationList: React.FC<{ education: Education[] }> = ({
  education,
}) => {
  if (!education || education?.length === 0) {
    return <LabelText>No Education added by user Yet</LabelText>;
  }
  return (
    <EducationContainer>
      {education.map((edu) => (
        <EducationItem key={edu._id}>
          <ButtonsSection>
            <LabelText>School</LabelText>
            <ValueText>{edu.school}</ValueText>
          </ButtonsSection>
          <ButtonsSection>
            <LabelText>Degree</LabelText>
            <ValueText>{edu.degree}</ValueText>
          </ButtonsSection>
          <ButtonsSection>
            <LabelText>Field of Study</LabelText>
            <ValueText>{edu.fieldOfStudy}</ValueText>
          </ButtonsSection>
          <ButtonsSection>
            <LabelText>Start Date</LabelText>
            <ValueText>{`${edu.startMonth} ${edu.startYear}`}</ValueText>
          </ButtonsSection>
          <ButtonsSection>
            <LabelText>End Date</LabelText>
            <ValueText>
              {edu.currentlyStudying
                ? "Currently Studying"
                : `${edu.endMonth} ${edu.endYear}`}
            </ValueText>
          </ButtonsSection>
        </EducationItem>
      ))}
    </EducationContainer>
  );
};
export const EmploymentHistory: React.FC<{
  education: IUserType["employmentHistory"];
}> = ({ education }) => {
  if (!education || education?.length === 0) {
    return <LabelText>No Employment history added by user Yet</LabelText>;
  }
  return (
    <EducationContainer>
      {education.map((edu) => (
        <EducationItem key={edu._id}>
          <ButtonsSection>
            <LabelText>Job Designation</LabelText>
            <ValueText>{edu.jobDesignation}</ValueText>
          </ButtonsSection>
          <ButtonsSection>
            <LabelText>Company Name</LabelText>
            <ValueText>{edu.companyName}</ValueText>
          </ButtonsSection>
          <ButtonsSection>
            <LabelText>Currently Working</LabelText>
            <ValueText>
              {" "}
              {edu.currentlyWorking
                ? "Currently Working"
                : `Currently Not working`}
            </ValueText>
          </ButtonsSection>
          <ButtonsSection>
            <LabelText>Start Month</LabelText>
            <ValueText>{edu.startMonth}</ValueText>
          </ButtonsSection>
          <ButtonsSection>
            <LabelText>Start Year</LabelText>
            <ValueText>{edu.startMonth}</ValueText>
          </ButtonsSection>
        </EducationItem>
      ))}
    </EducationContainer>
  );
};
export const renderSkills = (skills: string[]) => {
  if (!skills || skills?.length === 0) {
    return <LabelText>No skills added by user Yet</LabelText>;
  }
  return skills.map((skill, index) => (
    <div
      key={index}
      style={{
        borderRadius: 25,
        height: 30,
        padding: 8,
        background: "rgb(184, 171, 250)",
        border: "1px solid #F1EFFB",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography
        style={{
          fontSize: 14,
          fontWeight: 500,
          color: "#fff",
        }}
      >
        {skill}
      </Typography>
    </div>
  ));
};

export const RenderIcons = ({
  onDelete,
  icons,
}: {
  icons: { icon: string; _id: string }[];
  onDelete?: (id: string) => void;
}) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [open, setOpen] = useState<null | string>(null);
  const handleDelete = async (id: string) => {
    if (onDelete) {
      onDelete(id);
      setOpen(null);
    }
  };
  if (icons?.length === 0) {
    return <ValueText>No Icons</ValueText>;
  }
  return (
    <div
      style={{
        display: "flex",
        gap: 80,
        flexWrap: "wrap", // Allows wrapping when there are too many icons
      }}
    >
      {icons.map(({ icon, _id }) => (
        <div
          key={_id}
          className="icon-item"
          style={{
            position: "relative",
            margin: "10px",
            cursor: "pointer", // Show pointer cursor when hovering the icon
          }}
          onMouseEnter={() => setHoveredId(_id)} // Show X on hover
          onMouseLeave={() => setHoveredId(null)} // Hide X when not hovered
        >
          <img
            src={getIconsUrl(icon)}
            alt={`Icon ${_id}`}
            style={{ display: "block", width: "50px", height: "50px" }} // Adjust image size as needed
          />
          {open === _id && (
            <DeleteModal
              loading={false}
              title="Are you sure you wanna delete this Icon"
              itemName="Icon"
              onDelete={() => {
                handleDelete(_id);
              }}
              isOpen={true}
              onClose={() => {
                setOpen(null);
              }}
            ></DeleteModal>
          )}
          {hoveredId === _id && (
            <div
              onClick={() => {
                setOpen(_id);
              }} // Log when X is clicked
              style={{
                position: "absolute",
                top: "0",
                right: "0",
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                color: "white",
                borderRadius: "50%",
                width: "20px",
                height: "20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              X
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
